/**
 * Subida de archivos grandes REANUDABLE por trozos (contrato `/api/subidas`),
 * pensada para el celular del aseador en terreno: videos de hasta 20 min y
 * cientos de MB por datos móviles.
 *
 * Por qué no `apiFetch`: `fetch` no entrega progreso de subida ni deja
 * cancelar con limpieza; aquí se usa `XMLHttpRequest` (progreso real por bytes
 * enviados). El protocolo de sesión es el mismo de `apiFetch` (apiClient.ts):
 * cookie HttpOnly (`withCredentials`) + header `X-CSRF-Token` en las mutaciones,
 * auto-recuperación del CSRF desincronizado (403 "csrf" → `/auth/me` → un
 * reintento) y cierre de sesión ante un 401.
 *
 * Qué resiste:
 *  - Cortes de red y timeouts: reintenta cada trozo con backoff exponencial
 *    (hasta `maxIntentos` fallos seguidos) y, antes de reenviar, pregunta al
 *    servidor cuántos bytes tiene (`GET /subidas/:id`) para retomar desde ahí.
 *  - Offset desalineado (409 `OFFSET_INVALIDO`): retoma desde `recibidos`.
 *  - Sin internet: se pausa sola (no gasta intentos) y sigue al volver 'online'.
 *  - Recarga de la página: la sesión queda en localStorage (24 h) asociada a
 *    ruta+nombre+tamaño+lastModified; si el servidor ya no la conoce, empieza
 *    de nuevo.
 *  - Pantalla apagada: pide un Wake Lock mientras sube y, al volver a primer
 *    plano con la conexión muerta, aborta el trozo colgado para reintentar ya.
 *
 * Archivos chicos (<= 8 MB, p. ej. fotos comprimidas): un solo POST multipart
 * al endpoint del módulo, también por XHR para tener progreso igual.
 *
 * Lo que NO hace (a propósito): reintentar solo un POST no idempotente cuando
 * ya se envió todo el cuerpo y no llegó respuesta — podría duplicar el
 * archivo; en ese caso avisa que revise la lista antes de volver a subir.
 */

import { API_BASE_URL, getCsrfToken, setCsrfToken } from "../services/apiClient";
import { broadcastLogout } from "./authBroadcast";
import { MedidorVelocidad, calcularEta, calcularPorcentaje, mimeDeArchivo } from "./colaSubida";
import {
  MSG_CANCELADA,
  MSG_SUBIDA_SIN_CONEXION,
} from "./erroresRed";

// ─── Constantes y tipos ──────────────────────────────────────────────────────

/** Tamaño de trozo por defecto (el servidor lo confirma en `/iniciar`). */
export const CHUNK_BYTES_DEFAULT = 8 * 1024 * 1024;
/** Hasta este tamaño se usa un solo POST multipart (sin sesión reanudable). */
export const UMBRAL_MULTIPART_BYTES = 8 * 1024 * 1024;
/** Tope de peso por archivo (espejo de MAX_MEDIA_UPLOAD_BYTES del backend). */
export const MAX_SUBIDA_BYTES = 3 * 1024 * 1024 * 1024;
export const MAX_INTENTOS_DEFAULT = 6;
/** Sin ningún byte de avance durante este tiempo = conexión muerta. */
export const INACTIVIDAD_MS_DEFAULT = 60_000;
/** Las sesiones guardadas en el navegador caducan igual que en el servidor. */
export const TTL_SESION_MS = 24 * 60 * 60 * 1000;
const CLAVE_SESIONES = "subidas.reanudables.v1";
/** Tope de espera para la fase "procesando" del servidor (probe + miniatura). */
const TIMEOUT_PROCESANDO_MS = 15 * 60 * 1000;
const MIN_MS_ENTRE_PROGRESOS = 250;
const MAX_REINICIOS_SESION = 2;
const MAX_CONFLICTOS_SEGUIDOS = 10;
const MSG_PAUSA_SIN_CONEXION =
  "Sin conexión: la subida sigue sola cuando vuelva la señal.";

export type EstadoSubida =
  | "preparando"
  | "subiendo"
  | "reintentando"
  | "sin_conexion"
  | "procesando"
  | "completa";

export interface ProgresoSubida {
  /** Bytes del archivo ya enviados (acumulado, incluye lo que el servidor ya tenía). */
  bytes: number;
  total: number;
  porcentaje: number;
  velocidadBps: number;
  etaSeg: number | null;
  estado: EstadoSubida;
  /** Fallos seguidos del trozo actual (solo en "reintentando"). */
  intento?: number;
  mensaje?: string | null;
}

export type CodigoErrorSubida =
  | "RED"
  | "CANCELADA"
  | "SERVIDOR"
  | "VALIDACION"
  | "SESION"
  | "SESION_PERDIDA"
  | "AMBIGUO";

export class ErrorSubida extends Error {
  readonly codigo: CodigoErrorSubida;
  readonly status?: number;
  /** El cuerpo de la petición alcanzó a enviarse completo antes de fallar. */
  readonly enviado: boolean;
  /** Abortado por nosotros para reintentar (no cuenta como fallo). */
  readonly sinPenalizar: boolean;
  /** `error.code` que mandó el servidor (TIPO_NO_PERMITIDO, IMAGEN_DEMASIADO_GRANDE…), si lo hubo. */
  readonly codigoServidor?: string;
  /** Fuerza la clasificación de `esErrorNoReintentable` (true = Reintentar sí sirve). */
  readonly reintentable?: boolean;

  constructor(
    codigo: CodigoErrorSubida,
    mensaje: string,
    extra: {
      status?: number;
      enviado?: boolean;
      sinPenalizar?: boolean;
      codigoServidor?: string;
      reintentable?: boolean;
    } = {},
  ) {
    super(mensaje);
    this.name = "ErrorSubida";
    this.codigo = codigo;
    this.status = extra.status;
    this.enviado = extra.enviado ?? false;
    this.sinPenalizar = extra.sinPenalizar ?? false;
    this.codigoServidor = extra.codigoServidor;
    this.reintentable = extra.reintentable;
  }
}

/** Códigos del servidor que NO mejoran reintentando: el archivo en sí no sirve. */
const CODIGOS_NO_REINTENTABLES: ReadonlySet<string> = new Set([
  "TIPO_NO_PERMITIDO",
  "VALIDACION",
  "IMAGEN_DEMASIADO_GRANDE",
]);

/** Status HTTP que significan «el servidor rechazó este archivo»: repetir el envío da lo mismo. */
const STATUS_NO_REINTENTABLES: ReadonlySet<number> = new Set([400, 413, 415, 422]);

/**
 * ¿Reintentar es inútil? Verdadero para los rechazos del archivo (tipo no permitido,
 * imagen falsa, demasiado grande: TIPO_NO_PERMITIDO, VALIDACION, IMAGEN_DEMASIADO_GRANDE,
 * 400, 413, 415, 422). Falso para red, 5xx, 408, 429 y lo desconocido: ahí Reintentar sí puede servir.
 */
export function esErrorNoReintentable(e: unknown): boolean {
  if (!(e instanceof ErrorSubida)) return false;
  if (e.reintentable !== undefined) return !e.reintentable;
  if (e.codigo === "VALIDACION") return true;
  if (e.codigoServidor && CODIGOS_NO_REINTENTABLES.has(e.codigoServidor)) return true;
  if (e.codigo === "SERVIDOR" && e.status != null) {
    return STATUS_NO_REINTENTABLES.has(e.status) && e.codigoServidor !== "TIPO_DE_CUERPO_INVALIDO";
  }
  return false;
}

export interface OpcionesSubida {
  onProgreso?: (p: ProgresoSubida) => void;
  signal?: AbortSignal;
  chunkBytes?: number;
  maxIntentos?: number;
  /** Espera base del backoff (ms): base * 2^(n-1), con tope `backoffMaxMs`. */
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  inactividadMs?: number;
  /** Inyectable para pruebas. */
  crearXhr?: () => XMLHttpRequest;
  ahora?: () => number;
}

// ─── Utilidades chicas ───────────────────────────────────────────────────────

/** Texto tal cual lo mandó el servidor (solo para decisiones internas, p. ej. detectar «csrf»). */
function textoCrudoDeServidor(json: any): string {
  const m =
    json?.message ||
    (typeof json?.error === "string" ? json.error : json?.error?.message);
  return typeof m === "string" ? m : "";
}

/** Sin el tipo técnico (application/x-msdownload…): la aseadora no sabe qué es. */
export const MSG_TIPO_NO_PERMITIDO =
  "Ese tipo de archivo no se puede subir. Usa fotos, videos o documentos PDF/Office/texto.";
const MSG_ARCHIVO_NO_COINCIDE =
  "El contenido del archivo no coincide con su tipo (por ejemplo, una foto dañada). Elige otro archivo.";

/** Códigos del backend con texto propio en español (el `message` puede venir en inglés o ser técnico). */
const MENSAJE_POR_CODIGO: Record<string, string> = {
  SIN_ESPACIO: "El servidor no tiene espacio para recibir este archivo. Avisa al administrador.",
  DEMASIADAS_SUBIDAS:
    "Tienes demasiadas subidas sin terminar. Espera unos minutos e inténtalo de nuevo.",
  RATE_LIMITED: "Hay demasiadas solicitudes seguidas. Espera un minuto e inténtalo de nuevo.",
  TIPO_DE_CUERPO_INVALIDO: "El servidor no aceptó el formato del envío. Toca Reintentar.",
  TIPO_NO_PERMITIDO: MSG_TIPO_NO_PERMITIDO,
};

const MSG_ERROR_5XX = "El servidor tuvo un problema. Inténtalo de nuevo en unos minutos.";

/** Textos fijos por status HTTP, cuando el servidor no trae un mensaje en español utilizable. */
function mensajePorStatus(status: number): string {
  if (status >= 500) return MSG_ERROR_5XX;
  switch (status) {
    case 408:
      return "El servidor tardó demasiado en responder. Toca Reintentar.";
    case 413:
      return "El archivo es demasiado grande para subirlo.";
    case 415:
      return "Este tipo de archivo no se puede subir.";
    case 429:
      return "Hay demasiadas solicitudes seguidas. Espera un minuto e inténtalo de nuevo.";
    case 400:
      return "El servidor no aceptó este archivo.";
    case 403:
      return "No tienes permiso para subir este archivo.";
    case 404:
      return "El servidor no encontró lo que se buscaba. Toca Reintentar.";
    default:
      return `El servidor respondió con un error (${status}). Toca Reintentar.`;
  }
}

/** Heurística: ¿el texto está en español? (el backend responde en español salvo sus errores genéricos). */
const pareceEspanol = (t: string) =>
  /[áéíóúñ¿¡]/i.test(t) ||
  /\b(el|la|los|las|de|del|que|no|se|un|una|archivo|video|tu|tus|debe|supera|para|con|por|ya|hay)\b/i.test(t);

/**
 * Mensaje en español para mostrar al usuario. Orden: código conocido del backend
 * (`error.code`) → 5xx con texto fijo (el backend manda «Internal server error»)
 * → mensaje del servidor si está en español → texto fijo según el status.
 * Nada en inglés llega a la UI.
 */
function mensajeDeServidor(json: any, status: number): string {
  const codigo = json?.error?.code;
  if (typeof codigo === "string" && MENSAJE_POR_CODIGO[codigo]) return MENSAJE_POR_CODIGO[codigo];
  if (status >= 500) return MSG_ERROR_5XX;
  const m = textoCrudoDeServidor(json);
  // Estos dos llegan en español pero con el mime técnico dentro: texto amable, sin mime.
  if (/^s*tipo no permitido/i.test(m)) return MSG_TIPO_NO_PERMITIDO;
  if (/^s*archivo rechazado/i.test(m)) return MSG_ARCHIVO_NO_COINCIDE;
  if (m && pareceEspanol(m)) return m;
  return mensajePorStatus(status);
}

/** Error del servidor con su texto en español y su `error.code` (para clasificar si Reintentar sirve). */
function errorDeServidor(
  json: any,
  status: number,
  codigo: CodigoErrorSubida = "SERVIDOR",
  extra: { reintentable?: boolean } = {},
): ErrorSubida {
  const cs = json?.error?.code;
  return new ErrorSubida(codigo, mensajeDeServidor(json, status), {
    status,
    codigoServidor: typeof cs === "string" ? cs : undefined,
    ...extra,
  });
}

function parseJson(texto: string): any {
  if (!texto) return null;
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

const esMutacion = (metodo: string) =>
  !["GET", "HEAD", "OPTIONS"].includes(metodo.toUpperCase());

const dormir = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ErrorSubida("CANCELADA", MSG_CANCELADA));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new ErrorSubida("CANCELADA", MSG_CANCELADA));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

const sinConexion = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/** Espera el evento 'online' (o cancelación). Resuelve de inmediato si hay red. */
function esperarConexion(signal?: AbortSignal): Promise<void> {
  if (!sinConexion()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const alVolver = () => {
      limpiar();
      resolve();
    };
    const alCancelar = () => {
      limpiar();
      reject(new ErrorSubida("CANCELADA", MSG_CANCELADA));
    };
    const limpiar = () => {
      window.removeEventListener("online", alVolver);
      signal?.removeEventListener("abort", alCancelar);
    };
    window.addEventListener("online", alVolver);
    if (signal?.aborted) return alCancelar();
    signal?.addEventListener("abort", alCancelar, { once: true });
  });
}

// ─── Wake Lock ───────────────────────────────────────────────────────────────

/**
 * Pide que la pantalla no se apague mientras se sube (el navegador móvil pausa
 * o mata las peticiones largas con la pantalla bloqueada). Se re-pide al volver
 * a primer plano porque el sistema libera el lock al ocultar la página.
 * Devuelve la función para soltarlo. Nunca lanza: sin soporte, no hace nada.
 */
export function mantenerPantallaActiva(): () => void {
  let activo = true;
  let sentinel: { release?: () => Promise<void> | void } | null = null;

  const pedir = async () => {
    try {
      if (!activo || sentinel) return;
      const wl = (navigator as any)?.wakeLock;
      if (!wl || typeof wl.request !== "function") return;
      const s = await wl.request("screen");
      if (!activo) {
        try {
          await s?.release?.();
        } catch {
          /* nada */
        }
        return;
      }
      sentinel = s;
      try {
        s.addEventListener?.("release", () => {
          if (sentinel === s) sentinel = null;
        });
      } catch {
        /* nada */
      }
    } catch {
      sentinel = null;
    }
  };

  const alCambiarVisibilidad = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      void pedir();
    }
  };

  try {
    document.addEventListener("visibilitychange", alCambiarVisibilidad);
  } catch {
    /* nada */
  }
  void pedir();

  return () => {
    activo = false;
    try {
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    } catch {
      /* nada */
    }
    const s = sentinel;
    sentinel = null;
    try {
      void s?.release?.();
    } catch {
      /* nada */
    }
  };
}

// ─── Sesiones guardadas (reanudar tras recargar) ─────────────────────────────

interface RegistroSesion {
  uploadId: string;
  creada: number;
  size: number;
}

/** Identidad estable del archivo: ruta destino + nombre + tamaño + lastModified. */
export function huellaArchivo(file: File, contexto = ""): string {
  return `${contexto}|${file.name}|${file.size}|${file.lastModified}`;
}

function leerSesiones(ahora: number): Record<string, RegistroSesion> {
  try {
    const raw = localStorage.getItem(CLAVE_SESIONES);
    if (!raw) return {};
    const todo = JSON.parse(raw) as Record<string, RegistroSesion>;
    const vigentes: Record<string, RegistroSesion> = {};
    for (const [k, v] of Object.entries(todo ?? {})) {
      if (v && typeof v.uploadId === "string" && ahora - Number(v.creada) < TTL_SESION_MS) {
        vigentes[k] = v;
      }
    }
    return vigentes;
  } catch {
    return {};
  }
}

function escribirSesiones(s: Record<string, RegistroSesion>): void {
  try {
    if (Object.keys(s).length === 0) localStorage.removeItem(CLAVE_SESIONES);
    else localStorage.setItem(CLAVE_SESIONES, JSON.stringify(s));
  } catch {
    /* localStorage no disponible (modo privado, cuota): se sube igual, sin reanudar tras recargar */
  }
}

export function sesionGuardada(huella: string, ahora = Date.now()): RegistroSesion | null {
  return leerSesiones(ahora)[huella] ?? null;
}

function guardarSesion(huella: string, reg: RegistroSesion, ahora: number): void {
  const s = leerSesiones(ahora);
  s[huella] = reg;
  escribirSesiones(s);
}

/** Olvida la sesión guardada de este archivo (tras terminar, cancelar o perderse). */
export function olvidarSesionSubida(huella: string): void {
  const s = leerSesiones(Date.now());
  if (huella in s) {
    delete s[huella];
    escribirSesiones(s);
  }
}

/** Huellas de las subidas que están corriendo ahora (nunca se limpian como «huérfanas»). */
const huellasEnCurso = new Set<string>();

/**
 * Descarta la sesión de subida de un archivo: la borra en el servidor y la olvida
 * en el navegador. Se usa al Cancelar/Quitar un ítem que quedó en error o
 * cancelado: sin esto la sesión (hasta 3 GB reservados) sigue viva hasta 24 h y
 * 5 así bloquean todas las subidas nuevas con 429. Mejor esfuerzo: nunca lanza.
 * `ruta` debe ser la misma que se usó al subir (forma parte de la huella).
 */
export async function descartarSubida(
  file: File,
  ruta = "",
  crearXhr?: () => XMLHttpRequest,
): Promise<void> {
  try {
    const huella = huellaArchivo(file, ruta);
    if (huellasEnCurso.has(huella)) return; // corriendo ahora: la cancelación la limpia sola
    const reg = sesionGuardada(huella);
    olvidarSesionSubida(huella);
    if (reg) await cancelarSesionRemota(reg.uploadId, crearXhr);
  } catch {
    /* mejor esfuerzo */
  }
}

/**
 * Cancela en el servidor y olvida las sesiones guardadas en este navegador que no
 * están corriendo ahora. Red de seguridad ante el tope de sesiones simultáneas
 * (429 DEMASIADAS_SUBIDAS). Devuelve cuántas liberó.
 */
async function limpiarSesionesHuerfanas(crearXhr?: () => XMLHttpRequest): Promise<number> {
  const guardadas = leerSesiones(Date.now());
  const huerfanas = Object.entries(guardadas).filter(([h]) => !huellasEnCurso.has(h));
  if (huerfanas.length === 0) return 0;
  const s = leerSesiones(Date.now());
  for (const [h] of huerfanas) delete s[h];
  escribirSesiones(s);
  await Promise.all(huerfanas.map(([, reg]) => cancelarSesionRemota(reg.uploadId, crearXhr)));
  return huerfanas.length;
}

// ─── Capa HTTP (XHR) ─────────────────────────────────────────────────────────

interface RespuestaHttp {
  status: number;
  json: any;
}

type MotivoAborto = "cancelada" | "inactividad" | "reanudacion";

interface ParamsPeticion {
  metodo: "GET" | "POST" | "PUT" | "DELETE";
  /** Ruta relativa a `API_BASE_URL`, como en `apiFetch`. */
  ruta: string;
  cuerpo?: Blob | FormData | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** 0 = sin control de inactividad. */
  inactividadMs?: number;
  /** Tope total de la petición (ms). */
  timeoutMs?: number;
  onSubida?: (loaded: number, total: number) => void;
  /** El cuerpo terminó de enviarse (falta la respuesta). */
  onEnviado?: () => void;
  /** Entrega la función que aborta ESTA petición (para el reintento por visibilidad). */
  registrarAbortador?: (abortar: ((m: MotivoAborto) => void) | null) => void;
  crearXhr: () => XMLHttpRequest;
}

/** Una petición XHR. Resuelve con cualquier status HTTP; rechaza solo por red/aborto. */
function peticion(p: ParamsPeticion): Promise<RespuestaHttp> {
  return new Promise<RespuestaHttp>((resolve, reject) => {
    if (p.signal?.aborted) {
      reject(new ErrorSubida("CANCELADA", MSG_CANCELADA));
      return;
    }
    const xhr = p.crearXhr();
    let enviado = false;
    let motivo: MotivoAborto | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const terminar = () => {
      if (timer) clearTimeout(timer);
      p.signal?.removeEventListener("abort", alCancelar);
      p.registrarAbortador?.(null);
    };
    const armar = () => {
      if (!p.inactividadMs) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        motivo = "inactividad";
        xhr.abort();
      }, p.inactividadMs);
    };
    function alCancelar() {
      motivo = "cancelada";
      xhr.abort();
    }

    xhr.open(p.metodo, `${API_BASE_URL}${p.ruta}`, true);
    xhr.withCredentials = true;
    if (p.timeoutMs) xhr.timeout = p.timeoutMs;
    xhr.setRequestHeader("Accept", "application/json");
    for (const [k, v] of Object.entries(p.headers ?? {})) xhr.setRequestHeader(k, v);
    if (esMutacion(p.metodo)) {
      const csrf = getCsrfToken();
      if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);
    }

    xhr.upload.onprogress = (ev: ProgressEvent) => {
      armar();
      if (ev.lengthComputable) p.onSubida?.(ev.loaded, ev.total);
    };
    xhr.upload.onload = () => {
      enviado = true;
      armar();
      p.onEnviado?.();
    };
    xhr.onload = () => {
      terminar();
      resolve({ status: xhr.status, json: parseJson(xhr.responseText) });
    };
    const fallaDeRed = () => {
      terminar();
      reject(
        new ErrorSubida("RED", MSG_SUBIDA_SIN_CONEXION, {
          enviado,
          sinPenalizar: motivo === "reanudacion",
        }),
      );
    };
    xhr.onerror = fallaDeRed;
    xhr.ontimeout = fallaDeRed;
    xhr.onabort = () => {
      if (motivo === "cancelada") {
        terminar();
        reject(new ErrorSubida("CANCELADA", MSG_CANCELADA));
      } else {
        fallaDeRed();
      }
    };

    p.signal?.addEventListener("abort", alCancelar, { once: true });
    p.registrarAbortador?.((m) => {
      motivo = m;
      xhr.abort();
    });
    armar();
    xhr.send((p.cuerpo ?? null) as XMLHttpRequestBodyInit | null);
  });
}

/** Re-sincroniza el CSRF en memoria con la cookie vigente (igual que apiClient). */
async function refrescarCsrf(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/me`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    const fresco = typeof data?.csrfToken === "string" ? data.csrfToken : null;
    if (fresco) setCsrfToken(fresco);
    return fresco;
  } catch {
    return null;
  }
}

/**
 * Cierre de sesión ante un 401, con las mismas piezas que `forceLogout` de
 * apiClient (que no se exporta). El POST de logout va por XHR para no reentrar
 * al interceptor global de `fetch` (un 401 ahí volvería a disparar el logout).
 */
function cerrarSesionPor401(): void {
  try {
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
  } catch {
    /* nada */
  }
  setCsrfToken(null);
  broadcastLogout();
  try {
    const x = new XMLHttpRequest();
    x.open("POST", `${API_BASE_URL}/auth/logout`, true);
    x.withCredentials = true;
    x.send(null);
  } catch {
    /* nada */
  }
  window.location.href = "/login";
}

/** `peticion` + auto-recuperación CSRF + manejo de 401. */
async function llamar(p: ParamsPeticion): Promise<RespuestaHttp> {
  let r = await peticion(p);
  if (r.status === 403 && esMutacion(p.metodo) && /csrf/i.test(textoCrudoDeServidor(r.json))) {
    const fresco = await refrescarCsrf();
    if (fresco) r = await peticion(p);
  }
  if (r.status === 401) {
    cerrarSesionPor401();
    throw new ErrorSubida("SESION", "Tu sesión expiró. Vuelve a iniciar sesión.", {
      status: 401,
    });
  }
  return r;
}

const ok = (r: RespuestaHttp) => r.status >= 200 && r.status < 300;

/** El servidor ya no conoce la sesión (vencida, borrada o de otro usuario). */
const esSesionPerdida = (r: RespuestaHttp) =>
  r.status === 404 ||
  r.status === 410 ||
  (r.status === 403 && !/csrf/i.test(textoCrudoDeServidor(r.json)));

// ─── Emisor de progreso ──────────────────────────────────────────────────────

function crearEmisor(total: number, opts: OpcionesSubida) {
  const ahora = opts.ahora ?? (() => Date.now());
  const medidor = new MedidorVelocidad();
  let ultimaEmision = 0;
  let ultimoEstado: EstadoSubida | null = null;
  let ultimoBytes = 0;

  const emitir = (
    estado: EstadoSubida,
    bytes: number,
    extra: { intento?: number; mensaje?: string | null; forzar?: boolean } = {},
  ) => {
    const t = ahora();
    const b = Math.max(0, Math.min(total, bytes));
    ultimoBytes = b;
    if (estado === "subiendo") medidor.registrar(b, t);
    const cambioEstado = estado !== ultimoEstado;
    if (!cambioEstado && !extra.forzar && t - ultimaEmision < MIN_MS_ENTRE_PROGRESOS) return;
    ultimaEmision = t;
    ultimoEstado = estado;
    const bps = estado === "subiendo" ? medidor.velocidadBps() : 0;
    const p: ProgresoSubida = {
      bytes: b,
      total,
      porcentaje: estado === "completa" ? 100 : calcularPorcentaje(b, total),
      velocidadBps: bps,
      etaSeg:
        estado === "subiendo"
          ? calcularEta(total - b, bps)
          : estado === "procesando" || estado === "completa"
            ? 0
            : null,
      estado,
      intento: extra.intento,
      mensaje: extra.mensaje ?? null,
    };
    try {
      opts.onProgreso?.(p);
    } catch {
      /* un callback roto no debe tumbar la subida */
    }
  };

  return {
    emitir,
    ultimosBytes: () => ultimoBytes,
    reiniciarMedidor: () => medidor.reiniciar(),
  };
}

// ─── Subida reanudable por trozos ────────────────────────────────────────────

function backoffMs(fallos: number, opts: OpcionesSubida): number {
  const base = opts.backoffBaseMs ?? 1000;
  const tope = opts.backoffMaxMs ?? 30_000;
  const espera = Math.min(tope, base * 2 ** Math.max(0, fallos - 1));
  return espera + Math.random() * espera * 0.2;
}

/** Borra la sesión en el servidor (mejor esfuerzo; sin signal para que corra tras cancelar). */
export function cancelarSesionRemota(
  uploadId: string,
  crearXhr: () => XMLHttpRequest = () => new XMLHttpRequest(),
): Promise<void> {
  return llamar({ metodo: "DELETE", ruta: `/subidas/${encodeURIComponent(uploadId)}`, crearXhr })
    .then(() => undefined)
    .catch(() => undefined);
}

export interface ResultadoSubida {
  uploadId: string;
}

/**
 * Sube `file` al contrato `/api/subidas` en trozos y devuelve el `uploadId` de
 * la sesión completa. El llamador lo usa después con el POST del módulo
 * (`confirmarSubidaEnModulo`). `huella` identifica la sesión para reanudarla
 * tras recargar; si no se da, se calcula solo con nombre+tamaño+fecha.
 */
export async function subirArchivoReanudable(
  file: File,
  opts: OpcionesSubida & { huella?: string } = {},
): Promise<ResultadoSubida> {
  const crearXhr = opts.crearXhr ?? (() => new XMLHttpRequest());
  const ahora = opts.ahora ?? (() => Date.now());
  const signal = opts.signal;
  const total = file.size;

  if (!(total > 0)) {
    throw new ErrorSubida("VALIDACION", `El archivo "${file.name}" está vacío.`);
  }
  if (total > MAX_SUBIDA_BYTES) {
    throw new ErrorSubida("VALIDACION", `El archivo "${file.name}" supera el máximo permitido (3 GB).`);
  }
  if (signal?.aborted) throw new ErrorSubida("CANCELADA", MSG_CANCELADA);

  const huella = opts.huella ?? huellaArchivo(file);
  huellasEnCurso.add(huella);
  const maxIntentos = opts.maxIntentos ?? MAX_INTENTOS_DEFAULT;
  const inactividadMs = opts.inactividadMs ?? INACTIVIDAD_MS_DEFAULT;
  const { emitir } = crearEmisor(total, opts);

  // Aborta el trozo en vuelo (si lo hay) — lo usa el reintento al volver a primer plano.
  let abortarActual: ((m: MotivoAborto) => void) | null = null;
  const progresoReal = { t: ahora() }; // instante del último byte enviado
  const registrarAbortador: ParamsPeticion["registrarAbortador"] = (fn) => {
    abortarActual = fn;
  };
  const alVolverAPrimerPlano = () => {
    if (document.visibilityState !== "visible") return;
    if (abortarActual && ahora() - progresoReal.t > 4000) abortarActual("reanudacion");
  };
  document.addEventListener("visibilitychange", alVolverAPrimerPlano);
  const soltarPantalla = mantenerPantallaActiva();

  let uploadId: string | null = null;

  /** Devuelve `recibidos` del servidor o lanza SESION_PERDIDA si ya no la conoce. */
  const consultarSesion = async (id: string): Promise<{ recibidos: number; completa: boolean }> => {
    const r = await llamar({
      metodo: "GET",
      ruta: `/subidas/${encodeURIComponent(id)}`,
      signal,
      inactividadMs,
      crearXhr,
      registrarAbortador,
    });
    if (ok(r)) {
      const rec = Number(r.json?.data?.recibidos);
      if (!Number.isFinite(rec) || rec < 0) {
        throw new ErrorSubida("SERVIDOR", "Respuesta inválida del servidor al consultar la subida.");
      }
      const recibidos = Math.min(rec, total);
      return { recibidos, completa: r.json?.data?.estado === "completa" && recibidos >= total };
    }
    if (esSesionPerdida(r)) {
      throw new ErrorSubida("SESION_PERDIDA", "La sesión de subida ya no existe.", { status: r.status });
    }
    throw errorDeServidor(r.json, r.status);
  };

  /** Intenta retomar la sesión guardada; si no sirve, crea una nueva. */
  const abrirSesion = async (
    usarGuardada: boolean,
  ): Promise<{ id: string; recibidos: number; completa: boolean; chunk: number }> => {
    if (usarGuardada) {
      const guardada = sesionGuardada(huella, ahora());
      if (guardada && guardada.size === total) {
        for (let n = 1; ; n++) {
          try {
            const est = await consultarSesion(guardada.uploadId);
            return {
              id: guardada.uploadId,
              recibidos: est.recibidos,
              completa: est.completa,
              chunk: opts.chunkBytes ?? CHUNK_BYTES_DEFAULT,
            };
          } catch (e) {
            if (!(e instanceof ErrorSubida)) throw e;
            if (e.codigo === "RED") {
              if (n >= maxIntentos) throw e;
              await esperarConexion(signal);
              await dormir(backoffMs(n, opts), signal);
              continue;
            }
            if (e.codigo === "SESION_PERDIDA") break; // empezar de nuevo
            throw e;
          }
        }
        olvidarSesionSubida(huella);
      }
    }
    let limpiezaHecha = false;
    const iniciar = () =>
      llamar({
        metodo: "POST",
        ruta: "/subidas/iniciar",
        cuerpo: new Blob([JSON.stringify({ nombre: file.name, mime: mimeDeArchivo(file), size: total })], {
          type: "application/json",
        }),
        headers: { "Content-Type": "application/json" },
        signal,
        inactividadMs,
        crearXhr,
        registrarAbortador,
      });
    for (let n = 1; ; n++) {
      try {
        await esperarConexion(signal);
        let r = await iniciar();
        // Tope de sesiones simultáneas: antes de rendirse se liberan las que este
        // navegador dejó abandonadas (error, cancelación o cierre de la app) y se
        // reintenta UNA vez.
        if (!ok(r) && r.json?.error?.code === "DEMASIADAS_SUBIDAS" && !limpiezaHecha) {
          limpiezaHecha = true;
          if ((await limpiarSesionesHuerfanas(crearXhr)) > 0) r = await iniciar();
        }
        if (!ok(r)) {
          throw errorDeServidor(r.json, r.status, r.status === 413 || r.status === 400 ? "VALIDACION" : "SERVIDOR");
        }
        const id = String(r.json?.data?.id ?? "");
        if (!id) throw new ErrorSubida("SERVIDOR", "El servidor no devolvió el id de la subida.");
        const chunk = Number(r.json?.data?.chunkBytes) > 0 ? Number(r.json.data.chunkBytes) : CHUNK_BYTES_DEFAULT;
        guardarSesion(huella, { uploadId: id, creada: ahora(), size: total }, ahora());
        return { id, recibidos: 0, completa: false, chunk: opts.chunkBytes ?? chunk };
      } catch (e) {
        // /iniciar no es idempotente pero sin cuerpo enviado completo no hay sesión creada:
        // reintentar es seguro mientras `enviado` sea falso.
        if (e instanceof ErrorSubida && e.codigo === "RED" && n < maxIntentos) {
          emitir("reintentando", 0, { intento: n, mensaje: "Reintentando la conexión…" });
          await dormir(backoffMs(n, opts), signal);
          continue;
        }
        throw e;
      }
    }
  };

  /** Envía los trozos que faltan y cierra la sesión. Devuelve el id cuando el servidor tiene todo. */
  const enviar = async (usarGuardada: boolean): Promise<string> => {
    emitir("preparando", 0, { forzar: true });
    const sesion = await abrirSesion(usarGuardada);
    uploadId = sesion.id;
    let recibidos = sesion.recibidos;
    let completa = sesion.completa;
    const chunk = sesion.chunk;
    let fallos = 0;
    let conflictos = 0;
    emitir("subiendo", recibidos, { forzar: true });

    for (let vueltaCierre = 0; vueltaCierre < 3; vueltaCierre++) {
      // 1) Trozos hasta que el servidor tenga todo.
      while (!completa && recibidos < total) {
        if (signal?.aborted) throw new ErrorSubida("CANCELADA", MSG_CANCELADA);
        if (sinConexion()) {
          emitir("sin_conexion", recibidos, { mensaje: MSG_PAUSA_SIN_CONEXION });
          await esperarConexion(signal);
          emitir("subiendo", recibidos, { forzar: true });
        }

        const ini = recibidos;
        const fin = Math.min(ini + chunk, total);
        let motivoReintento: ErrorSubida | null = null;
        try {
          progresoReal.t = ahora();
          const r = await llamar({
            metodo: "PUT",
            ruta: `/subidas/${encodeURIComponent(sesion.id)}?offset=${ini}`,
            cuerpo: file.slice(ini, fin),
            headers: { "Content-Type": "application/octet-stream" },
            signal,
            inactividadMs,
            onSubida: (loaded, tot) => {
              progresoReal.t = ahora();
              // loaded/total incluye el overhead del request: se proyecta al largo del trozo.
              const parte = tot > 0 ? Math.min(fin - ini, ((fin - ini) * loaded) / tot) : 0;
              emitir("subiendo", ini + parte);
            },
            crearXhr,
            registrarAbortador,
          });

          if (ok(r)) {
            const rec = Number(r.json?.data?.recibidos);
            recibidos = Number.isFinite(rec) && rec >= 0 ? Math.min(rec, total) : fin;
            fallos = 0;
            conflictos = 0;
            emitir("subiendo", recibidos, { forzar: true });
            continue;
          }
          if (r.status === 409) {
            conflictos++;
            if (conflictos > MAX_CONFLICTOS_SEGUIDOS) {
              throw new ErrorSubida(
                "SERVIDOR",
                "El servidor y el celular no coinciden en el avance de la subida. Toca Reintentar.",
                { status: 409 },
              );
            }
            // OFFSET_INVALIDO trae `recibidos`; SUBIDA_COMPLETA (ya estaba todo) no: se pregunta.
            const rec = Number(r.json?.recibidos ?? r.json?.error?.recibidos);
            if (Number.isFinite(rec) && rec >= 0) {
              recibidos = Math.min(rec, total);
            } else {
              const est = await consultarSesion(sesion.id);
              recibidos = est.recibidos;
              completa = est.completa;
            }
            emitir("subiendo", recibidos, { forzar: true });
            continue; // retomar desde donde dice el servidor, sin penalizar
          }
          if (esSesionPerdida(r)) {
            throw new ErrorSubida("SESION_PERDIDA", "La sesión de subida ya no existe.", { status: r.status });
          }
          // 507 (sin espacio en el servidor) no mejora reintentando: se informa de una vez.
          if ((r.status >= 500 && r.status !== 507) || r.status === 408 || r.status === 429) {
            motivoReintento = errorDeServidor(r.json, r.status);
          } else {
            throw errorDeServidor(r.json, r.status, r.status === 413 || r.status === 400 ? "VALIDACION" : "SERVIDOR");
          }
        } catch (e) {
          if (!(e instanceof ErrorSubida) || e.codigo !== "RED") throw e;
          motivoReintento = e;
        }

        // Falla reintentable (red / 5xx): backoff, resincronizar y seguir.
        const falla: ErrorSubida = motivoReintento;
        const penaliza = !(falla.sinPenalizar || sinConexion());
        if (penaliza) fallos++;
        if (fallos > maxIntentos) {
          throw falla.codigo === "RED" ? new ErrorSubida("RED", MSG_SUBIDA_SIN_CONEXION) : falla;
        }
        if (sinConexion()) {
          emitir("sin_conexion", recibidos, { mensaje: MSG_PAUSA_SIN_CONEXION });
          await esperarConexion(signal);
        } else if (penaliza) {
          emitir("reintentando", recibidos, { intento: fallos, mensaje: "Se cortó la conexión; reintentando…" });
          await dormir(backoffMs(fallos, opts), signal);
        }
        try {
          const est = await consultarSesion(sesion.id);
          recibidos = est.recibidos;
          completa = est.completa;
        } catch (e) {
          // Si la consulta también falla por red, el próximo PUT corrige con un 409.
          if (!(e instanceof ErrorSubida) || e.codigo !== "RED") throw e;
        }
        emitir("subiendo", recibidos, { forzar: true });
      }

      // 2) Cerrar la sesión (`completar` solo verifica recibidos === size: es idempotente).
      if (completa) return sesion.id;
      for (let n = 1; ; n++) {
        try {
          const r = await llamar({
            metodo: "POST",
            ruta: `/subidas/${encodeURIComponent(sesion.id)}/completar`,
            signal,
            inactividadMs,
            crearXhr,
            registrarAbortador,
          });
          if (ok(r)) return sesion.id;
          if (esSesionPerdida(r)) {
            throw new ErrorSubida("SESION_PERDIDA", "La sesión de subida ya no existe.", { status: r.status });
          }
          if (r.status === 400) {
            // Dice que faltan bytes: volver a los trozos desde lo que realmente tenga.
            const est = await consultarSesion(sesion.id);
            if (est.recibidos >= total) {
              throw errorDeServidor(r.json, 400, "SERVIDOR", { reintentable: true });
            }
            recibidos = est.recibidos;
            break;
          }
          if (r.status < 500 || n >= maxIntentos) {
            throw errorDeServidor(r.json, r.status);
          }
        } catch (e) {
          if (!(e instanceof ErrorSubida) || e.codigo !== "RED" || n >= maxIntentos) throw e;
        }
        await esperarConexion(signal);
        emitir("reintentando", total, { intento: n });
        await dormir(backoffMs(n, opts), signal);
      }
    }
    throw new ErrorSubida("SERVIDOR", "No se pudo cerrar la subida. Toca Reintentar.");
  };

  try {
    let usarGuardada = true;
    for (let reinicio = 0; ; reinicio++) {
      try {
        const id = await enviar(usarGuardada);
        emitir("procesando", total, { forzar: true });
        return { uploadId: id };
      } catch (e) {
        if (e instanceof ErrorSubida && e.codigo === "SESION_PERDIDA" && reinicio < MAX_REINICIOS_SESION) {
          olvidarSesionSubida(huella);
          usarGuardada = false;
          continue;
        }
        throw e;
      }
    }
  } catch (e) {
    if (e instanceof ErrorSubida) {
      if (e.codigo === "CANCELADA") {
        // Cancelación: liberar disco en el servidor y no dejar la sesión para reanudar.
        olvidarSesionSubida(huella);
        if (uploadId) void cancelarSesionRemota(uploadId, crearXhr);
      } else if (e.codigo === "VALIDACION" || e.codigo === "SESION_PERDIDA") {
        // Error que no mejora reintentando: también se libera la sesión en el servidor
        // (si no, queda viva hasta 24 h y cuenta para el tope de sesiones).
        const id = uploadId ?? sesionGuardada(huella)?.uploadId ?? null;
        olvidarSesionSubida(huella);
        if (id) void cancelarSesionRemota(id, crearXhr);
      }
      // RED / SERVIDOR / SESION: se conserva la sesión para reanudar con Reintentar.
    }
    throw e;
  } finally {
    huellasEnCurso.delete(huella);
    document.removeEventListener("visibilitychange", alVolverAPrimerPlano);
    soltarPantalla();
  }
}

// ─── POST del módulo ─────────────────────────────────────────────────────────

function agregarUploadId(ruta: string, uploadId: string): string {
  return `${ruta}${ruta.includes("?") ? "&" : "?"}uploadId=${encodeURIComponent(uploadId)}`;
}

const MSG_AMBIGUO =
  "Se perdió la conexión justo al terminar. Revisa si el archivo ya aparece en la lista antes de volver a subirlo.";

/**
 * El POST de siempre del endpoint del módulo, pero SIN archivo multipart y con
 * `?uploadId=`: el servidor arma el archivo desde la sesión completa y hace el
 * resto igual (validación, miniatura, duración). Devuelve el body JSON.
 */
export async function confirmarSubidaEnModulo<T = any>(
  ruta: string,
  uploadId: string,
  opts: Pick<OpcionesSubida, "signal" | "crearXhr"> = {},
): Promise<T> {
  const crearXhr = opts.crearXhr ?? (() => new XMLHttpRequest());
  try {
    await esperarConexion(opts.signal);
    const r = await llamar({
      metodo: "POST",
      ruta: agregarUploadId(ruta, uploadId),
      signal: opts.signal,
      inactividadMs: 0,
      timeoutMs: TIMEOUT_PROCESANDO_MS,
      crearXhr,
    });
    if (!ok(r)) {
      throw errorDeServidor(r.json, r.status);
    }
    return r.json as T;
  } catch (e) {
    // Sin respuesta tras mandar la orden: no sabemos si el servidor alcanzó a guardarlo.
    if (e instanceof ErrorSubida && e.codigo === "RED") throw new ErrorSubida("AMBIGUO", MSG_AMBIGUO);
    throw e;
  }
}

/** Un solo POST multipart con progreso (archivos chicos). */
async function subirMultipart<T>(ruta: string, file: File, opts: OpcionesSubida): Promise<T> {
  const crearXhr = opts.crearXhr ?? (() => new XMLHttpRequest());
  const total = file.size;
  const { emitir } = crearEmisor(total, opts);
  const maxIntentos = Math.min(opts.maxIntentos ?? MAX_INTENTOS_DEFAULT, 3);
  const fd = new FormData();
  fd.append("file", file);
  const soltarPantalla = mantenerPantallaActiva();
  try {
    emitir("preparando", 0, { forzar: true });
    for (let n = 1; ; n++) {
      try {
        if (sinConexion()) {
          emitir("sin_conexion", 0, { mensaje: "Sin conexión: se envía cuando vuelva la señal." });
          await esperarConexion(opts.signal);
        }
        emitir("subiendo", 0, { forzar: true });
        const r = await llamar({
          metodo: "POST",
          ruta,
          cuerpo: fd,
          signal: opts.signal,
          inactividadMs: opts.inactividadMs ?? INACTIVIDAD_MS_DEFAULT,
          timeoutMs: TIMEOUT_PROCESANDO_MS,
          onSubida: (loaded, tot) => {
            const bytes = tot > 0 ? (total * loaded) / tot : 0;
            emitir("subiendo", bytes);
          },
          onEnviado: () => emitir("procesando", total, { forzar: true }),
          crearXhr,
        });
        if (!ok(r)) {
          throw errorDeServidor(r.json, r.status);
        }
        emitir("completa", total, { forzar: true });
        return r.json as T;
      } catch (e) {
        if (!(e instanceof ErrorSubida) || e.codigo !== "RED") throw e;
        // Sin haber terminado de enviar el cuerpo el servidor no guardó nada: reintento seguro.
        if (!e.enviado && n < maxIntentos) {
          emitir("reintentando", 0, { intento: n, mensaje: "Se cortó la conexión; reintentando…" });
          await dormir(backoffMs(n, opts), opts.signal);
          continue;
        }
        throw e.enviado ? new ErrorSubida("AMBIGUO", MSG_AMBIGUO) : e;
      }
    }
  } finally {
    soltarPantalla();
  }
}

/**
 * Sube `file` al endpoint de un módulo (`ruta` relativa a la API, p. ej.
 * `/aseos/12/notas/aseador/archivos`) y devuelve el JSON de la respuesta.
 * <= 8 MB: un POST multipart; más: sesión reanudable + POST con `?uploadId=`.
 */
export async function subirArchivoAModulo<T = any>(
  ruta: string,
  file: File,
  opts: OpcionesSubida = {},
): Promise<T> {
  // Algunos Android entregan el archivo sin tipo MIME: se infiere por la extensión
  // para que el servidor no lo rechace por "tipo vacío".
  const mime = mimeDeArchivo(file);
  if (mime && mime !== file.type) {
    file = new File([file], file.name, { type: mime, lastModified: file.lastModified });
  }
  if (file.size <= UMBRAL_MULTIPART_BYTES) return subirMultipart<T>(ruta, file, opts);

  const huella = huellaArchivo(file, ruta);
  const { uploadId } = await subirArchivoReanudable(file, { ...opts, huella });
  const { emitir } = crearEmisor(file.size, opts);
  try {
    const cuerpo = await confirmarSubidaEnModulo<T>(ruta, uploadId, {
      signal: opts.signal,
      crearXhr: opts.crearXhr,
    });
    olvidarSesionSubida(huella);
    emitir("completa", file.size, { forzar: true });
    return cuerpo;
  } catch (e) {
    if (e instanceof ErrorSubida && (e.codigo === "CANCELADA" || (e.codigo === "SERVIDOR" && (e.status ?? 500) < 500))) {
      // El módulo rechazó el archivo (tipo, duración…) o se canceló: no tiene sentido conservar la sesión.
      olvidarSesionSubida(huella);
      void cancelarSesionRemota(uploadId, opts.crearXhr);
    }
    throw e;
  }
}
